import {
  Button,
  Drawer,
  Row,
  Space,
  Form,
  Select,
  Input,
  notification,
} from 'antd';
import {
  useAddEmployeeMutation,
  useGetAllGpsEmployeeQuery,
  useGetEmployeeAccountingMethodsQuery,
  useUpdateEmployeeMutation,
} from 'app/api';

import NumberInput from 'app/components/NumberInput/NumberInput';
import { selectCompanies } from 'app/containers/Company/slice/selectors';
import { useGlobalSlice } from 'app/containers/Global/slice';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IOption } from 'types';
import { getInteger } from 'utils/helpers';
import { getEditEmployeeFormInitialValues } from '../../helpers';
import {
  EMPLOYEE_ACCOUNTING_METHODS_MAP,
  IEmployeeFormValues,
  IAddEmployeePayload,
  IEmployee,
} from '../../types';

interface IProps {
  isOpen: boolean;
  employee?: IEmployee;

  onClose: () => void;
}
const EmployeeDrawer: FC<IProps> = ({ isOpen, onClose, employee }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [form] = Form.useForm();
  const envId = useSelector(selectSelectedEnvId) as number;

  // Company Options
  const allCompanies = useSelector(selectCompanies);
  const companyOptions: IOption[] | undefined = allCompanies?.map(company => ({
    value: company.id,
    label: company.name,
  }));

  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >();

  // GPS Employee Options
  const {
    currentData: allGpsEmployee,
    isFetching: isGpsEmployeeLoading,
    error: gpsEmployeeError,
  } = useGetAllGpsEmployeeQuery({ envId }, { skip: !envId });

  const gpsEmployeeOptions: IOption[] | undefined = allGpsEmployee?.map(
    gpsEmployee => ({
      value: gpsEmployee.id,
      label: gpsEmployee.name,
      companyId: gpsEmployee.companyId,
      disabled:
        employee?.id !== gpsEmployee.garageEmployeeId && gpsEmployee.assigned,
    }),
  );

  // Reset gps employee if error on loading options
  useEffect(() => {
    if (gpsEmployeeError) {
      form.setFieldsValue({ gpsEmployeeId: undefined });
      handleError(gpsEmployeeError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsEmployeeError]);

  // Accounting Methods Options
  const {
    data: accountingMethods,
    isFetching: isAccountingMethodsLoading,
  } = useGetEmployeeAccountingMethodsQuery();

  const accountingMethodOptions:
    | IOption<number>[]
    | undefined = accountingMethods?.map(method => ({
    value: method.id,
    label: method.name,
  }));

  const [selectedAccountingMethods, setSelectedAccountingMethods] = useState<
    number[]
  >([]);

  // Set edit employee data on open
  useEffect(() => {
    if (isOpen && employee) {
      const values = getEditEmployeeFormInitialValues(employee);
      setSelectedAccountingMethods(values.accountingMethods);
      setSelectedCompanyId(values.companyId);
      form.setFieldsValue(values);
    }
  }, [employee, isOpen, form]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setSelectedCompanyId(undefined);
      setSelectedAccountingMethods([]);
    }
  }, [isOpen, form]);

  const [
    addEmployee,
    { isLoading: isAddSubmitLoading },
  ] = useAddEmployeeMutation();

  const [
    updateEmployee,
    { isLoading: isUpdateSubmitLoading },
  ] = useUpdateEmployeeMutation();

  const isSubmitLoading = isAddSubmitLoading || isUpdateSubmitLoading;

  const onFinish = async (values: IEmployeeFormValues) => {
    try {
      if (!envId || isSubmitLoading) {
        return;
      }
      const {
        haSellPrice,
        haCostPrice,
        hourCostPrice,
        hourSellPrice,
        salary,
        ...formValues
      } = values;

      const methods: {
        costPrice: number | undefined;
        id: number;
        sellPrice: number | undefined;
      }[] =
        values.accountingMethods?.map(methodId => {
          if (methodId === EMPLOYEE_ACCOUNTING_METHODS_MAP.ha) {
            return {
              costPrice: getInteger(values.haCostPrice),
              sellPrice: getInteger(values.haSellPrice),
              id: methodId,
            };
          }
          return {
            costPrice: getInteger(values.hourCostPrice),
            sellPrice: getInteger(values.hourSellPrice),
            id: methodId,
          };
        }) || [];

      const payload: IAddEmployeePayload = {
        ...formValues,
        envId,
        salary: getInteger(salary),
        lastName: 'One',
        birthYear: values.birthYear ? Number(values.birthYear) : undefined,
        accountingMethods: methods,
      };

      let successMsg = '';
      if (employee) {
        await updateEmployee({
          ...payload,
          employeeId: employee.id,
        }).unwrap();
        successMsg = t(
          'Employee.Drawer.SuccessfullyUpdated',
          'Employee successfully updated',
        );
      } else {
        await addEmployee(payload).unwrap();
        successMsg = t(
          'Employee.Drawer.SuccessfullyAdded',
          'Employee successfully added',
        );
      }

      onClose();
      notification.success({
        message: successMsg,
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  const title = employee
    ? t('Employee.Drawer.EditTitle', 'Edit employee')
    : t('Employee.Drawer.Title', 'Add employee');

  return (
    <Drawer
      title={title}
      visible={isOpen}
      forceRender={true}
      onClose={onClose}
      destroyOnClose
      placement="right"
      width={400}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose}>{t('Common.Cancel', 'Cancel')}</Button>

            <Button
              loading={isSubmitLoading}
              form="employeeForm"
              key="submit"
              htmlType="submit"
              type="primary"
            >
              {t('Common.Save', 'Save')}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical' }}
        requiredMark={true}
        id="employeeForm"
        onFinish={onFinish}
      >
        <Form.Item
          label={t('Employee.Name', 'Name Surname')}
          name="firstName"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Employee.Name', 'Name Surname')} />
        </Form.Item>

        <Form.Item label={t('Employee.Year', 'Year of birth')} name="birthYear">
          <NumberInput placeholder={t('Employee.Year', 'Year of birth')} />
        </Form.Item>

        <Form.Item
          label={t('Employee.PhoneNumber', 'Phone number')}
          name="phoneNumber"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Employee.PhoneNumber', 'Phone number')} />
        </Form.Item>

        <Form.Item
          label={t('Employee.Workplace', 'Workplace')}
          name="companyId"
          rules={[{ required: true }]}
        >
          <Select
            bordered={true}
            placeholder={t('Employee.Workplace', 'Workplace')}
            dropdownMatchSelectWidth={false}
            options={companyOptions}
            value={selectedCompanyId}
            onChange={value => setSelectedCompanyId(value)}
            disabled={Boolean(employee)}
          />
        </Form.Item>

        <Form.Item
          label={'SLIM ID'}
          name="gpsEmployeeId"
          rules={[{ required: false }]}
        >
          <Select
            onChange={(value, option) => {
              if ('companyId' in option && !selectedCompanyId) {
                setSelectedCompanyId(option.companyId);
                form.setFieldsValue({ companyId: option.companyId });
              }
            }}
            bordered={true}
            placeholder={'SLIM ID'}
            dropdownMatchSelectWidth={false}
            options={gpsEmployeeOptions}
            loading={isGpsEmployeeLoading}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          label={t('Employee.BasicSalary', 'Basic salary')}
          name="salary"
        >
          <NumberInput
            placeholder={t('Employee.BasicSalary', 'Basic salary')}
          />
        </Form.Item>

        <Form.Item
          label={t('Transport.CalculationMethod', 'Calculation method')}
          name="accountingMethods"
        >
          <Select
            mode="multiple"
            bordered={true}
            placeholder={t('Transport.CalculationMethod', 'Calculation method')}
            dropdownMatchSelectWidth={false}
            options={accountingMethodOptions}
            value={selectedAccountingMethods}
            onChange={value => setSelectedAccountingMethods(value)}
            allowClear={true}
            loading={isAccountingMethodsLoading}
          />
        </Form.Item>

        {selectedAccountingMethods.includes(
          EMPLOYEE_ACCOUNTING_METHODS_MAP.hours,
        ) && (
          <>
            <Form.Item
              label={t('Employee.HourCostPrice', 'Hour cost price')}
              name="hourCostPrice"
            >
              <NumberInput
                placeholder={t('Employee.HourCostPrice', 'Hour cost price')}
              />
            </Form.Item>

            <Form.Item
              label={t('Employee.HourSellPrice', 'Hour sell price')}
              name="hourSellPrice"
            >
              <NumberInput
                placeholder={t('Employee.HourSellPrice', 'Hour sell price')}
              />
            </Form.Item>
          </>
        )}

        {selectedAccountingMethods.includes(
          EMPLOYEE_ACCOUNTING_METHODS_MAP.ha,
        ) && (
          <>
            <Form.Item
              label={t('Transport.HaCostPrice', 'Ha cost price')}
              name="haCostPrice"
            >
              <NumberInput
                placeholder={t('Transport.HaCostPrice', 'Ha cost price')}
              />
            </Form.Item>

            <Form.Item
              label={t('Transport.HaSellPrice', 'Ha sell price')}
              name="haSellPrice"
            >
              <NumberInput
                placeholder={t('Transport.HaSellPrice', 'Ha sell price')}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default EmployeeDrawer;
